import type { EmailMessage } from "@redotech/effect-api-shared/models/shopper-ai/conversation/message/email";
import { sortMessagesByReplyChain } from "@redotech/util/topological-sort";
import { useCallback, useMemo, useState } from "react";
import type {
  EmailContact,
  FormattedEmailMessage,
} from "../components/email-message-card";

export interface EmailThreadViewState {
  /** Formatted messages ready for display */
  formattedMessages: FormattedEmailMessage[];
  /** Check if a specific message is expanded */
  isMessageExpanded: (messageId: string) => boolean;
  /** Toggle expansion state of a message */
  toggleMessageExpanded: (messageId: string) => void;
  /** Expand all messages */
  expandAll: () => void;
  /** Collapse all messages (keeps last message expanded) */
  collapseAll: () => void;
}

/**
 * Hook to manage email thread display state.
 * Combines message formatting with expansion state management.
 * By default, the last message is expanded and others are collapsed.
 */
export function useEmailThreadView(
  messages: EmailMessage[],
): EmailThreadViewState {
  // Sort messages chronologically with stable topological ordering for equal timestamps
  const sortedMessages = useMemo(() => {
    return sortMessagesByReplyChain(messages);
  }, [messages]);

  const lastMessageId = useMemo(() => {
    if (sortedMessages.length === 0) {
      return null;
    }
    return sortedMessages[sortedMessages.length - 1]?.id ?? null;
  }, [sortedMessages]);

  // Expansion state management
  const [expandedMessageIds, setExpandedMessageIds] = useState<Set<string>>(
    () => {
      const initialSet = new Set<string>();
      if (lastMessageId) {
        initialSet.add(lastMessageId);
      }
      return initialSet;
    },
  );

  const toggleMessageExpanded = useCallback((messageId: string) => {
    setExpandedMessageIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(messageId)) {
        newSet.delete(messageId);
      } else {
        newSet.add(messageId);
      }
      return newSet;
    });
  }, []);

  const isMessageExpanded = useCallback(
    (messageId: string) => {
      return expandedMessageIds.has(messageId);
    },
    [expandedMessageIds],
  );

  const expandAll = useCallback(() => {
    setExpandedMessageIds(new Set(messages.map((m) => m.id)));
  }, [messages]);

  const collapseAll = useCallback(() => {
    const newSet = new Set<string>();
    if (lastMessageId) {
      newSet.add(lastMessageId);
    }
    setExpandedMessageIds(newSet);
  }, [lastMessageId]);

  // Format messages for display
  const formattedMessages = useMemo(() => {
    return sortedMessages.map((message) => formatEmailMessage(message));
  }, [sortedMessages]);

  return {
    formattedMessages,
    isMessageExpanded,
    toggleMessageExpanded,
    expandAll,
    collapseAll,
  };
}

/**
 * Pure function to format a single email message for display.
 * Uses current branch field names: addressType, email, name
 */
function formatEmailMessage(message: EmailMessage): FormattedEmailMessage {
  const isMessageFromCustomer = message.origin === "external";
  const addresses = message.channelData?.addresses ?? null;

  // Extract sender from "from" address
  const fromAddress = addresses?.find((a) => a.addressType === "from");
  const senderName =
    fromAddress?.name ??
    fromAddress?.email ??
    (isMessageFromCustomer ? "Customer" : "Agent");
  const senderInitials = senderName.charAt(0).toUpperCase();

  // Extract all address types
  const toAddresses = addresses?.filter((a) => a.addressType === "to") ?? [];
  const ccAddresses = addresses?.filter((a) => a.addressType === "cc") ?? [];
  const bccAddresses = addresses?.filter((a) => a.addressType === "bcc") ?? [];

  // Structured address fields - from is always required
  const from: EmailContact = fromAddress
    ? toEmailContact(fromAddress)
    : { name: senderName, email: "" };
  const to = toAddresses.map(toEmailContact);
  const cc = ccAddresses.map(toEmailContact);
  const bcc = bccAddresses.map(toEmailContact);

  // Generate summary for collapsed view
  const allRecipients = [...toAddresses, ...ccAddresses, ...bccAddresses];
  const recipientSummary = formatRecipientSummary(allRecipients);

  const content = message.content ?? "";
  const htmlContent = message.channelData?.parsedHtmlBody ?? null;
  const preview = getMessagePreview(content);

  const formattedTime = formatMessageTime(message.sentAt);

  return {
    id: message.id,
    senderName,
    senderInitials,
    isMessageFromCustomer,
    from,
    to,
    cc,
    bcc,
    recipientSummary,
    content,
    htmlContent,
    timestamp: message.sentAt,
    formattedTime,
    preview,
  };
}

function getMessagePreview(content: string, maxLength = 100): string {
  if (!content) {
    return "(No content)";
  }
  const stripped = content.replace(/\s+/g, " ").trim();
  if (stripped.length <= maxLength) {
    return stripped;
  }
  return stripped.substring(0, maxLength - 3) + "...";
}

function formatMessageTime(date: Date): string {
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();

  if (isToday) {
    return date.toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "2-digit",
    });
  }

  const isThisYear = date.getFullYear() === now.getFullYear();
  if (isThisYear) {
    return date.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  }

  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatRecipientSummary(
  recipients: readonly { name: string | null; email: string }[],
): string | null {
  if (recipients.length === 0) {
    return null;
  }
  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  const first = recipients[0].name ?? recipients[0].email;
  if (recipients.length === 1) {
    return first;
  }
  return `${first}, +${recipients.length - 1} more`;
}

function toEmailContact(a: {
  name: string | null;
  email: string;
}): EmailContact {
  return { name: a.name, email: a.email };
}
